import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { ButtonModule } from 'primeng/button';
import { FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { debounceTime } from 'rxjs';
import { Core } from '../_core/core';
import { Metering } from '../_core/metering';
import { Spinner } from '../_core/spinner';
import { ToastService } from '../_core/toast.service';
import { ITenantMeter, ISendReadingPayload } from '../core.interface';
import { SendReadingHelpDialog } from './send-reading-help-dialog';

export type ReadingStatus = 'higher' | 'equal' | 'lower';

const READING_ALERT_DEBOUNCE_MS = 500;

export interface SentReadingItem {
  meterId: number;
  serialNumber: string;
  supplyType: string;
  location: string;
  lastReading: number;
  value: number;
  consumption: number;
  units: string;
  hasPhoto: boolean;
  success: boolean;
}

export interface SubmissionResult {
  succeeded: SentReadingItem[];
  failed: SentReadingItem[];
}

@Component({
  selector: 'app-send-reading',
  imports: [
    ButtonModule,
    DatePipe,
    DecimalPipe,
    FileUploadModule,
    SendReadingHelpDialog,
    InputNumberModule,
    LucideAngularModule,
    MessageModule,
    ReactiveFormsModule,
    SkeletonModule,
  ],
  templateUrl: './send-reading.html',
  styleUrl: './send-reading.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendReading implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly $core = inject(Core);
  private readonly $metering = inject(Metering);
  private readonly $toast = inject(ToastService);
  private readonly spinner = inject(Spinner);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly resultHeading = viewChild<ElementRef<HTMLElement>>('resultHeading');

  readonly form: FormGroup = this.fb.group({
    meters: this.fb.array([]),
  });

  readonly supplies = this.$metering.supplies;
  readonly loaded = signal(false);
  readonly isSubmitting = signal(false);
  readonly helpExpanded = signal(false);
  readonly submissionResult = signal<SubmissionResult | null>(null);
  readonly formRevision = signal(0);
  readonly previewUrls = signal<ReadonlyMap<number, string>>(new Map());
  /** Debounced statuses used only for equal/lower alerts (waits until user stops typing). */
  readonly alertStatuses = signal<ReadonlyMap<number, ReadingStatus | null>>(new Map());

  readonly hasMeters = computed(() => {
    this.formRevision();
    return this.meters.length > 0;
  });
  readonly showForm = computed(() => this.loaded() && this.hasMeters() && !this.submissionResult());
  readonly readyCount = computed(() => {
    this.formRevision();
    return this.meters.controls.filter((control) => this.isRowReady(control)).length;
  });
  readonly canSubmit = computed(() => this.readyCount() > 0 && !this.isSubmitting());

  get meters(): FormArray {
    return this.form.get('meters') as FormArray;
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formRevision.update((v) => v + 1);
      // Hide alerts while typing; they reappear after debounce when the user pauses.
      if (this.alertStatuses().size > 0) {
        this.alertStatuses.set(new Map());
      }
    });

    this.form.valueChanges
      .pipe(debounceTime(READING_ALERT_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshAlertStatuses());

    void this.loadData();
  }

  ngOnDestroy(): void {
    this.revokeAllPreviews();
  }

  async loadData(): Promise<void> {
    const tenantId = this.$core.selectedBuilding()?.tenantId;
    if (!tenantId) {
      this.loaded.set(true);
      return;
    }

    this.spinner.show();
    try {
      await this.loadSupplies(tenantId);
      await this.$metering.fetchTenantMeters(tenantId, false);
      this.rebuildForm(this.$metering.tenantMeters());
    } catch {
      this.$toast.error('No se pudieron cargar los contadores.');
      this.rebuildForm([]);
    } finally {
      this.spinner.hide();
      this.loaded.set(true);
    }
  }

  toggleHelp(): void {
    this.helpExpanded.update((v) => !v);
  }

  goHome(): void {
    void this.router.navigate(['/home']);
  }

  unitsFor(index: number): string {
    return String(this.meters.at(index).get('units')?.value ?? '');
  }

  /** Immediate status (submit / ready). Prefer `alertReadingStatus` for UI warnings. */
  readingStatus(index: number): ReadingStatus | null {
    this.formRevision();
    return this.statusForControl(this.meters.at(index));
  }

  /** Debounced status for equal/lower messages and related UI hints. */
  alertReadingStatus(index: number): ReadingStatus | null {
    const meterId = Number(this.meters.at(index).get('meterId')?.value);
    return this.alertStatuses().get(meterId) ?? null;
  }

  consumptionFor(index: number): number | null {
    this.formRevision();
    const control = this.meters.at(index);
    const value = control.get('value')?.value;
    const baseline = this.resolveLastReading(control);
    if (value == null || value === '' || baseline == null) {
      return null;
    }
    return Number(value) - baseline;
  }

  displayedLastReading(control: AbstractControl): number | null {
    return this.resolveLastReading(control);
  }

  isRowReady(control: AbstractControl): boolean {
    const valueCtrl = control.get('value');
    if (
      valueCtrl == null ||
      valueCtrl.value == null ||
      valueCtrl.value === '' ||
      valueCtrl.invalid
    ) {
      return false;
    }

    const status = this.statusForControl(control);
    if (status === 'lower' && control.get('attachment')?.value == null) {
      return false;
    }

    return true;
  }

  rowReady(index: number): boolean {
    this.formRevision();
    return this.isRowReady(this.meters.at(index));
  }

  hasAttachment(index: number): boolean {
    this.formRevision();
    return this.meters.at(index).get('attachment')?.value != null;
  }

  /** Red “photo required” UI only while lower and still missing the file. */
  photoRequired(index: number): boolean {
    return this.alertReadingStatus(index) === 'lower' && !this.hasAttachment(index);
  }

  previewUrl(meterId: number): string | null {
    return this.previewUrls().get(meterId) ?? null;
  }

  onFileSelect(event: FileSelectEvent, index: number): void {
    const file = event.files?.[0];
    if (!file) {
      return;
    }

    const group = this.meters.at(index);
    const meterId = Number(group.get('meterId')?.value);
    group.get('attachment')?.setValue(file);
    this.setPreview(meterId, file);
  }

  clearAttachment(index: number, fileUpload?: FileUpload): void {
    const group = this.meters.at(index);
    const meterId = Number(group.get('meterId')?.value);
    group.get('attachment')?.setValue(null);
    this.clearPreview(meterId);
    fileUpload?.clear();
  }

  async save(): Promise<void> {
    this.meters.controls.forEach((control) => {
      control.get('value')?.markAsDirty();
      control.get('value')?.markAsTouched();
    });

    const readyControls = this.meters.controls.filter((control) => this.isRowReady(control));
    const incompleteControls = this.meters.controls.filter((control) => {
      const value = control.get('value')?.value;
      const hasValue = value != null && value !== '';
      const hasAttachment = control.get('attachment')?.value != null;
      const lowerWithoutPhoto =
        this.statusForControl(control) === 'lower' && control.get('attachment')?.value == null;
      return ((hasValue || hasAttachment) && !this.isRowReady(control)) || lowerWithoutPhoto;
    });

    if (readyControls.length === 0) {
      const missingPhoto = this.meters.controls.some(
        (control) =>
          this.statusForControl(control) === 'lower' && control.get('attachment')?.value == null,
      );
      this.$toast.warning(
        missingPhoto
          ? 'Si la lectura es inferior a la anterior, debes adjuntar una foto para verificarla.'
          : 'Debes completar al menos una lectura válida para enviar.',
      );
      return;
    }

    if (incompleteControls.length > 0) {
      this.$toast.warning(
        `${incompleteControls.length} contador(es) tienen datos incompletos o inválidos y no se enviarán.`,
      );
    }

    const tenantId = this.$core.selectedBuilding()?.tenantId;
    if (!tenantId) {
      this.$toast.error('No hay una propiedad seleccionada.');
      return;
    }

    this.isSubmitting.set(true);
    this.spinner.show();

    const succeeded: SentReadingItem[] = [];
    const failed: SentReadingItem[] = [];

    try {
      for (const control of readyControls) {
        const item = this.toSentItem(control);
        const payload: ISendReadingPayload = {
          tenantId,
          meterId: String(item.meterId),
          value: item.value.toString().replace(',', '.'),
          attachment: control.get('attachment')?.value ?? null,
        };

        const ok = await this.$metering.sendReading(payload);
        if (ok) {
          succeeded.push({ ...item, success: true });
        } else {
          failed.push({ ...item, success: false });
        }
      }

      if (succeeded.length === 0) {
        this.$toast.error('No se pudo registrar ninguna lectura. Inténtalo de nuevo.');
        return;
      }

      if (failed.length > 0) {
        this.$toast.warning(
          `${succeeded.length} lectura(s) registrada(s). ${failed.length} no se pudieron enviar.`,
        );
      }

      this.submissionResult.set({ succeeded, failed });
      this.focusResult();
    } catch {
      this.$toast.error('Hubo un problema al enviar las lecturas.');
    } finally {
      this.isSubmitting.set(false);
      this.spinner.hide();
    }
  }

  private statusForControl(control: AbstractControl): ReadingStatus | null {
    const value = control.get('value')?.value;
    if (value == null || value === '') {
      return null;
    }
    const baseline = this.resolveLastReading(control);
    if (baseline == null) {
      return null;
    }
    const reading = Number(value);
    if (reading > baseline) {
      return 'higher';
    }
    if (reading === baseline) {
      return 'equal';
    }
    return 'lower';
  }

  private toSentItem(control: AbstractControl): SentReadingItem {
    const baseline = this.resolveLastReading(control) ?? 0;
    const value = Number(control.get('value')?.value);
    const consumption = value - baseline;

    return {
      meterId: Number(control.get('meterId')?.value),
      serialNumber: String(control.get('serialNumber')?.value ?? ''),
      supplyType: String(control.get('supplyType')?.value ?? ''),
      location: String(control.get('location')?.value ?? ''),
      lastReading: baseline,
      value,
      consumption,
      units: String(control.get('units')?.value ?? ''),
      hasPhoto: control.get('attachment')?.value != null,
      success: false,
    };
  }

  private async loadSupplies(tenantId: string): Promise<void> {
    if (this.$metering.isForTenant(tenantId) && this.$metering.supplies().length > 0) {
      return;
    }
    await this.$metering.getSupplies(tenantId);
  }

  private rebuildForm(data: ITenantMeter[]): void {
    this.revokeAllPreviews();
    this.meters.clear();
    data.forEach((m) => this.meters.push(this.createMeterGroup(m)));
    this.formRevision.update((v) => v + 1);
    this.refreshAlertStatuses();
  }

  private refreshAlertStatuses(): void {
    const next = new Map<number, ReadingStatus | null>();
    for (const control of this.meters.controls) {
      const meterId = Number(control.get('meterId')?.value);
      next.set(meterId, this.statusForControl(control));
    }
    this.alertStatuses.set(next);
  }

  private createMeterGroup(m: ITenantMeter): FormGroup {
    return this.fb.group({
      lastReading: [m.lastReading],
      lastRealReading: [m.lastRealReading],
      lastReadingDate: [m.lastReadingDate],
      location: [m.location],
      meterId: [m.meterId],
      serialNumber: [m.serialNumber],
      supplyType: [m.supplyType],
      units: [this.resolveUnits(m.supplyType)],
      value: [null as number | null, Validators.required],
      attachment: [null as File | null],
    });
  }

  /** Preferencia: lastRealReading; si no hay valor, lastReading. */
  private resolveLastReading(control: AbstractControl): number | null {
    const real = control.get('lastRealReading')?.value;
    if (real != null && real !== '') {
      return Number(real);
    }
    const last = control.get('lastReading')?.value;
    if (last != null && last !== '') {
      return Number(last);
    }
    return null;
  }

  private resolveUnits(supplyType: string): string {
    const normalized = supplyType.trim().toLowerCase();
    const matched = this.supplies().find((supply) => {
      const name = (supply.supplyName ?? supply.name ?? '').trim().toLowerCase();
      return name === normalized || name.includes(normalized) || normalized.includes(name);
    });

    const supplyTypeId = matched?.supplyTypeId ?? matched?.id;
    if (supplyTypeId === 1 || supplyTypeId === 2) {
      return 'm³';
    }
    if (supplyTypeId != null) {
      return 'kWh';
    }

    return normalized.includes('agua') ? 'm³' : 'kWh';
  }

  private setPreview(meterId: number, file: File): void {
    this.clearPreview(meterId);
    if (!file.type.startsWith('image/')) {
      return;
    }
    const url = URL.createObjectURL(file);
    const next = new Map(this.previewUrls());
    next.set(meterId, url);
    this.previewUrls.set(next);
  }

  private clearPreview(meterId: number): void {
    const current = this.previewUrls().get(meterId);
    if (current) {
      URL.revokeObjectURL(current);
      const next = new Map(this.previewUrls());
      next.delete(meterId);
      this.previewUrls.set(next);
    }
  }

  private revokeAllPreviews(): void {
    for (const url of this.previewUrls().values()) {
      URL.revokeObjectURL(url);
    }
    this.previewUrls.set(new Map());
  }

  private focusResult(): void {
    queueMicrotask(() => {
      const el = this.resultHeading()?.nativeElement;
      if (!el) {
        return;
      }
      el.focus({ preventScroll: true });
      el.scrollIntoView({
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  private prefersReducedMotion(): boolean {
    return (
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }
}
